import React, { useEffect } from "react";
import {
  AnchorButton,
  Button,
  Blockquote,
  Logo,
  Graphics,
  Intent,
} from "@careevolution/design-system";
import { Parallax, ParallaxProvider } from "react-scroll-parallax";

import RotatingHeader from "./RotatingHeader";
import LoginHeader from "./LoginHeader";

import useAuthenticationStore, { AuthenticationViews } from "./Store";
import LocalizedText from '../presentational/LocalizedText';
import useTranslate from "../../hooks/useTranslate";
import "./ParticipantLanding.css";
import LoginFooter from "./LoginFooter";
import { useAuthenticationConfiguration } from "./Configuration";
import { ProductNames } from "./Authentication";
import { getIdentifier, getOperatingSystem } from "./utils";
import CookiesBanner from "./CookiesBanner";

export default function () {
  const configuration = useAuthenticationConfiguration()
  const { state, dispatch, setActiveView, loadProject, checkLogin, validateIdentifier, getLoginCookie } = useAuthenticationStore()
  const { currentLanguage, redirectUrl } = state
  const { productName } = configuration;
  const translations = useTranslate(["detect-quote", "detect-quote-attribution", "powermom-quote", "powermom-quote-attribution", "download-the-app"])

  useEffect(() => {
    async function preparePage() {
      const knownUserEmailOrPhone = getLoginCookie()
      if (knownUserEmailOrPhone) {
        setActiveView(AuthenticationViews.knownUserLogin, { replace: true });
      }
      else if (productName !== ProductNames.MyDataHelps) {
        setActiveView(AuthenticationViews.splash)
      }

      let newRedirectUrl = redirectUrl
      const identifier = getIdentifier()
      if (identifier) {
        newRedirectUrl = await validateIdentifier(identifier, newRedirectUrl)
      }

      newRedirectUrl = await loadProject(newRedirectUrl);
      if (newRedirectUrl) {
        dispatch({ type: 'SET_LOGIN_CHECK_TIMER', payload: window.setInterval(checkLogin, 1500) });
      }
    }
    preparePage()
  }, []);

  var mobileOS = getOperatingSystem();
  const showDownloadSection = !(mobileOS == "iOS" || mobileOS == "Android");

  return (
    <ParallaxProvider>
      <LoginHeader productName={productName} />
      <main
        ce-style-variant="marketing"
        data-ce-design-system
        id="splash-page"
      >
        <div className="background-wrapper"></div>
        <section className="what-is-mdh dark-bg">
          <RotatingHeader />
          <div className="container col-2">
            <div className="text-wrap">
              <h4 className="what-is-mdh-description"><LocalizedText textKey="what-is-mdh-description" /></h4>
              <div className="button-group">
                <Button
                  className="primary-action"
                  size="large"
                  onClick={() => setActiveView(AuthenticationViews.join)}
                  intent={Intent.primary}
                >
                  <LocalizedText textKey="join-now" />
                </Button>
                <Button
                  className="login-button"
                  size="large"
                  onClick={() => setActiveView(AuthenticationViews.login)}
                >
                  <LocalizedText textKey="login" />
                </Button>
              </div>
              <ul className="icon-bullets">
                <li>
                  <span className="icon-wrap">
                    <img src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/research.svg" role="presentation" />
                  </span>
                  <span>
                    <strong><LocalizedText textKey="what-is-mdh-category-1-title" /></strong>
                    <LocalizedText textKey="what-is-mdh-category-1-description" />
                  </span>
                </li>
                <li>
                  <span className="icon-wrap">
                    <img src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/hospital.svg" role="presentation" />
                  </span>
                  <span>
                    <strong><LocalizedText textKey="what-is-mdh-category-2-title" /></strong>
                    <LocalizedText textKey="what-is-mdh-category-2-description" />
                  </span>
                </li>
                <li>
                  <span className="icon-wrap">
                    <img src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/watch.svg" role="presentation" />
                  </span>
                  <span>
                    <strong><LocalizedText textKey="what-is-mdh-category-3-title" /></strong>
                    <LocalizedText textKey="what-is-mdh-category-3-description" />
                  </span>
                </li>
              </ul>
            </div>
            <div className="image-wrap">
              <Parallax speed={5} className="moving-image hero-survey-1">
                <img src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/hero_survey.png" aria-hidden />
              </Parallax>
              <Parallax speed={5} className="moving-image hero-survey-2">
                <img src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/hero_survey_2.png" aria-hidden />
              </Parallax>
              <Parallax speed={-5} className="moving-image hero-symptom-shark">
                <img src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/hero_symptom_shark.png" aria-hidden />
              </Parallax>
            </div>
          </div>
        </section>
        <section className="trusted-by">
          <h4 className="header"><LocalizedText textKey="trusted-by" /></h4>
          <div className="trusted-logos container">
            <div className="logo-container">
              <img
                src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/trusted/trusted-quidel.png"
                alt="quidel"
              />
            </div>
            <div className="logo-container">
              <img
                src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/trusted/trusted-scripps.png"
                alt="scripps"
                style={{ opacity: 0.5 }}
              />
            </div>
            <div className="logo-container">
              <img
                src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/trusted/trusted-aou.png"
                alt="all of us"
                style={{ opacity: 0.7 }}
              />
            </div>
            <div className="logo-container">
              <img
                src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/trusted/trusted-umich.png"
                alt="univeristy of michigan"
                style={{ opacity: 0.7 }}
              />
            </div>
            <div className="logo-container">
              <img
                src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/trusted/trusted-massgen.png"
                alt="massachusetts general hospital"
              />
            </div>
            <div className="logo-container">
              <img
                src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/trusted/trusted-umass.png"
                alt="university of massachusetts medical school"
              />
            </div>
            <div className="logo-container">
              <img
                src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/trusted/trusted-als.png"
                alt="als association"
                style={{ opacity: 0.9 }}
              />
            </div>
            <div className="logo-container">
              <img
                src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/trusted/trusted-fhs.png"
                alt="framingham heart study"
              />
            </div>
          </div>
        </section>
        <section className="symptom-shark project">
          <div className="container col-2">
            <div className="text-wrap">
              <header>
                <h2><LocalizedText textKey="ss-title" /></h2>
              </header>
              <p><LocalizedText textKey="ss-description" /></p>
              <ul className="icon-bullets">
                <li>
                  <span className="icon-wrap">
                    <img src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/symptom_shark_table.svg" role=" presentational" />
                  </span>
                  <span>
                    <h5><LocalizedText textKey="ss-header-1" /></h5>
                    <LocalizedText textKey="ss-text-1" />
                  </span>
                </li>
                <li>
                  <span className="icon-wrap">
                    <img src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/symptom_shark_chart.svg" role=" presentational" />
                  </span>
                  <span>
                    <h5><LocalizedText textKey="ss-header-2" /></h5>
                    <LocalizedText textKey="ss-text-2" />
                  </span>
                </li>
                <li>
                  <span className="icon-wrap">
                    <img src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/symptom_shark_doctor.svg" role=" presentational" />
                  </span>
                  <span>
                    <h5><LocalizedText textKey="ss-header-3" /></h5>
                    <LocalizedText textKey="ss-text-3" />
                  </span>
                </li>
              </ul>
              <div className="button-group">
                <AnchorButton
                  className="primary-action"
                  size="large"
                  href="https://symptomshark.org"
                  target="_blank"
                >
                  <LocalizedText textKey="join-now" />
                </AnchorButton>
              </div>
            </div>
            <div className="image-wrap align-bottom" aria-hidden>
              <Parallax speed={-10} className="desktop-flex moving-image symptom-shark-1">
                <img className="symptom-shark-1a" src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/symptom_shark_1.png" alt="Screenshot of Symptom Shark App" />
                <img className="symptom-shark-1b" src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/symptom_shark_3.png" alt="Screenshot of Symptom Shark App" />
              </Parallax>
              <Parallax speed={-7} className="mobile-flex moving-image symptom-shark-1">
                <img src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/symptom_shark_1.png" alt="Screenshot of Symptom Shark App" />
              </Parallax>
              <Parallax speed={7} className="moving-image symptom-shark-2">
                <img src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/symptom_shark_2.png" alt="Screenshot of Symptom Shark App" />
              </Parallax>
              <Parallax speed={-7} className="mobile-flex moving-image symptom-shark-3">
                <img src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/symptom_shark_3.png" alt="Screenshot of Symptom Shark App" />
              </Parallax>
            </div>
          </div>
        </section>
        <section className="detect project">
          <div className="container col-2 align-stretch">
            <div className="text-wrap">
              <header>
                <h2><LocalizedText textKey="detect-title" /></h2>
              </header>
              <p><LocalizedText textKey="detect-description" /></p>
              <Blockquote
                quote={translations["detect-quote"]}
                cite={translations["detect-quote-cite"]}
              />
              <div className="button-group">
                <AnchorButton
                  className="primary-action"
                  size="large"
                  href={
                    currentLanguage === "en"
                      ? "https://detect.scripps.edu"
                      : "https://detect.scripps.edu/es"
                  }
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <LocalizedText textKey="join-now" />
                </AnchorButton>
              </div>
            </div>
            <div className="image-wrap" aria-hidden>
              <Parallax rotate={[20, 120]} className="detect-dial">
                <Graphics style="DialInscribed" color="black" />
              </Parallax>
              <Parallax speed={8} className="moving-image detect-calendar">
                <img src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/detect_calendar.png" alt=" Screenshot of Detect study app" />
              </Parallax>
              <Parallax speed={-8} className="moving-image detect-article">
                <img src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/detect_article.png" alt=" Screenshot of Detect study app" />
              </Parallax>
              <Parallax speed={3} className="moving-image detect-home">
                <img src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/detect_home.png" alt=" Screenshot of Detect study app" />
              </Parallax>
            </div>
          </div>
        </section>
        <section className="powermom project">
          <div className="container col-2 align-stretch">
            <div className="text-wrap">
              <header>
                <h2><LocalizedText textKey="powermom-title" /></h2>
              </header>
              <p><LocalizedText textKey="powermom-description" /></p>
              <Blockquote
                quote={translations["powermom-quote"]}
                cite={translations["powermom-quote-cite"]}
              />
              <div className="button-group">
                <AnchorButton
                  className="primary-action"
                  size="large"
                  href={
                    currentLanguage === "en"
                      ? "https://powermom.scripps.edu"
                      : "https://powermom.scripps.edu/es"
                  }
                  target="_blank"
                >
                  <LocalizedText textKey="join-now" />
                </AnchorButton>
              </div>
            </div>
            <div className="image-wrap" aria-hidden>
              <Parallax rotate={[120, 20]} className="powermom-dial">
                <Graphics style="DialEncircled" color="white" />
              </Parallax>
              <Parallax speed={2} className="moving-image powermom-2">
                <img src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/powermom_2.png" alt="Screenshot of Power Mom study" />
              </Parallax>
              <Parallax speed={-5} className="moving-image powermom-1">
                <img src="https://rkstudio-customer-assets.s3.amazonaws.com/CareEvolution/Images/ParticipantLanding/powermom_1.png" alt="Screenshot of Power Mom study" />
              </Parallax>
            </div>
          </div>
        </section>
        {
          showDownloadSection && (
            <section className="app-store">
              <h3 className="header">{translations["download-the-app"]}</h3>
              <div className="app-stores container">
                <a target="_blank" href="https://apps.apple.com/us/app/mydatahelps/id1286789190">
                  <img src={`https://appstorebadges.careevolutionapps.com/images/apple/black/logo-${currentLanguage}.svg`} alt={`${translations["download-the-app"]} iOS`} />
                </a>
                <a target="_blank" href="https://play.google.com/store/apps/details?id=com.careevolution.mydatahelps&hl=en_US">
                  <img src={`https://appstorebadges.careevolutionapps.com/images/google/black/logo-${currentLanguage}.svg`} alt={`${translations["download-the-app"]} Android`} />
                </a>
              </div>
            </section>
          )
        }
        <section className="login-section">
          <div className="container">
            <div className="login-card">
              <Logo
                brand="MyDataHelps"
                className="mdh-logo-login"
                style="default"
                color="black"
              />
              <p>
                <LocalizedText textKey="mdh-login-p" />
              </p>
              <div className="button-group">
                <Button
                  className="primary-action"
                  size="large"
                  onClick={() => setActiveView(AuthenticationViews.join)}
                  intent={Intent.primary}
                >
                  <LocalizedText textKey="join-now" />
                </Button>
                <Button
                  className="login-button"
                  size="large"
                  onClick={() => setActiveView(AuthenticationViews.login)}
                >
                  <LocalizedText textKey="login" />
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <LoginFooter />
      <CookiesBanner />
    </ParallaxProvider>
  );
}
